/**
 * Proposal schemas — priced snapshots sent to clients for signature.
 *
 * OPS 3.3: two proposal types, never mixed —
 *   RECURRING — items snapshot the client's compliance items, ONE billing
 *               schedule per proposal, drafts invoiced by the daily runner.
 *   ONE_OFF   — items snapshot engagement sub-items, invoiced once via the
 *               manual trigger on the signed proposal.
 * Items/clauses are COPIES: later edits to the source records or the clause
 * library never flow back into an existing proposal.
 */

import { z } from 'zod';
import { idSchema, iso8601Schema, emailSchema, urlSchema, currencySchema, sortOrderSchema } from './common';
import { publicDocumentBrandingSchema } from './invoice';

export const proposalStatusSchema = z.enum([
  'DRAFT',
  'SENT',
  'VIEWED',
  'SENT_BACK',
  'SIGNED',
  'DECLINED',
  'EXPIRED',
]);

export const proposalTypeSchema = z.enum(['RECURRING', 'ONE_OFF']);
export type ProposalType = z.infer<typeof proposalTypeSchema>;

/** OPS 3.7 step 5: PER_ITEM prices each snapshot row; LUMP_SUM quotes one fee
 *  for the whole scope — rows stay as a price-less scope list. */
export const proposalPricingModeSchema = z.enum(['PER_ITEM', 'LUMP_SUM']);
export type ProposalPricingMode = z.infer<typeof proposalPricingModeSchema>;

/** ONE billing schedule per recurring proposal (weekly billing not offered). */
export const billingScheduleSchema = z.enum(['MONTHLY', 'QUARTERLY', 'SEMI_ANNUAL', 'ANNUAL']);
export type BillingSchedule = z.infer<typeof billingScheduleSchema>;

export const proposalRecipientSchema = z.object({
  name: z.string().min(1).max(120),
  email: emailSchema,
  role: z.enum(['SIGNER', 'VIEWER', 'APPROVER']).default('SIGNER'),
});

export const proposalItemSourceTypeSchema = z.enum(['COMPLIANCE_ITEM', 'ENGAGEMENT_SUB_ITEM', 'CUSTOM']);
export type ProposalItemSourceType = z.infer<typeof proposalItemSourceTypeSchema>;

export const proposalItemInputSchema = z.object({
  sourceType: proposalItemSourceTypeSchema,
  complianceItemId: idSchema.nullish(),
  engagementSubItemId: idSchema.nullish(),
  title: z.string().min(1).max(200),
  description: z.string().max(1000).nullish(),
  /** Minor units, hand-set at compose time. */
  unitPrice: z.number().int().min(0),
  ordering: z.number().int().min(0).optional(),
});
export type ProposalItemInput = z.infer<typeof proposalItemInputSchema>;

export const proposalItemOutputSchema = proposalItemInputSchema.extend({
  id: idSchema,
  currency: currencySchema,
  description: z.string().max(1000).nullable(),
  complianceItemId: idSchema.nullable(),
  engagementSubItemId: idSchema.nullable(),
  ordering: z.number().int(),
});
export type ProposalItemOutput = z.infer<typeof proposalItemOutputSchema>;

export const proposalClauseInputSchema = z.object({
  libraryClauseId: idSchema.nullish(),
  title: z.string().min(1).max(200),
  body: z.string().min(1).max(10000),
  ordering: z.number().int().min(0).optional(),
});
export type ProposalClauseInput = z.infer<typeof proposalClauseInputSchema>;

export const proposalClauseOutputSchema = proposalClauseInputSchema.extend({
  id: idSchema,
  libraryClauseId: idSchema.nullable(),
  ordering: z.number().int(),
});
export type ProposalClauseOutput = z.infer<typeof proposalClauseOutputSchema>;

export const proposalOutputSchema = z.object({
  id: idSchema,
  type: proposalTypeSchema,
  engagementId: idSchema.nullable(),
  clientId: idSchema.nullable(),
  clientName: z.string().max(200).nullable(),
  /** Client contact email — the auto-added SIGNER (OPS 3.5 B: the shared
   *  composer/edit card renders it as the read-only signer row). */
  clientEmail: z.string().nullable(),
  feeAmount: z.number().int().nullable(),
  feeCurrency: currencySchema.nullable(),
  pricingMode: proposalPricingModeSchema,
  /** Minor units; set iff pricingMode = LUMP_SUM (feeAmount mirrors it). */
  lumpSumAmount: z.number().int().nullable(),
  engagementLetterUrl: urlSchema.nullable(),
  documensoEnvelopeId: z.string().nullable(),
  status: proposalStatusSchema,
  subject: z.string().max(200),
  body: z.string().max(10000),
  recipients: z.array(proposalRecipientSchema),
  /** Public signing URL — only returned on creation. */
  signingUrl: urlSchema.nullable(),
  expiresAt: iso8601Schema.nullable(),
  signedAt: iso8601Schema.nullable(),
  declinedAt: iso8601Schema.nullable(),
  billingSchedule: billingScheduleSchema.nullable(),
  billingStartDate: iso8601Schema.nullable(),
  nextInvoiceDate: iso8601Schema.nullable(),
  invoicingPaused: z.boolean(),
  invoicedAt: iso8601Schema.nullable(),
  items: z.array(proposalItemOutputSchema),
  clauses: z.array(proposalClauseOutputSchema),
  branding: publicDocumentBrandingSchema.optional(),
  createdAt: iso8601Schema,
  updatedAt: iso8601Schema,
});
export type ProposalOutput = z.infer<typeof proposalOutputSchema>;

export const createProposalInputSchema = z.object({
  subject: z.string().min(1).max(200),
  body: z.string().min(1).max(10000),
  feeAmount: z.number().int().nullable().optional(),
  feeCurrency: currencySchema.nullable().optional(),
  engagementLetterUrl: urlSchema.nullable().optional(),
  recipients: z.array(proposalRecipientSchema).min(1).max(10),
  expiresAt: iso8601Schema.nullable().optional(),
});
export type CreateProposalInput = z.infer<typeof createProposalInputSchema>;

/** OPS 3.3 composer — creates the priced snapshot in one call. */
export const composeProposalInputSchema = z.object({
  type: proposalTypeSchema,
  clientId: idSchema,
  /** Required for ONE_OFF (engagement sub-items live on the engagement). */
  engagementId: idSchema.nullish(),
  subject: z.string().min(1).max(200),
  body: z.string().max(10000).default(''),
  currency: currencySchema,
  pricingMode: proposalPricingModeSchema.default('PER_ITEM'),
  /** Required (≥ 0, minor units) when pricingMode = LUMP_SUM; ignored otherwise. */
  lumpSumAmount: z.number().int().min(0).nullish(),
  items: z.array(proposalItemInputSchema).min(1).max(100),
  billingSchedule: billingScheduleSchema.nullish(),
  /** First invoice period anchor; defaults to the signing date when omitted. */
  billingStartDate: iso8601Schema.nullish(),
  clauses: z.array(proposalClauseInputSchema).max(50).default([]),
  recipients: z.array(proposalRecipientSchema).min(1).max(10),
  expiresAt: iso8601Schema.nullish(),
  engagementLetterUrl: urlSchema.nullish(),
});
export type ComposeProposalInput = z.infer<typeof composeProposalInputSchema>;

/** Source records the composer can snapshot from. */
export const proposalSourcesOutputSchema = z.object({
  complianceItems: z.array(z.object({
    id: idSchema,
    serviceType: z.string(),
    periodValue: z.string().nullable(),
    status: z.string(),
    jurisdiction: z.string().nullable(),
    isRecurring: z.boolean(),
    recurrenceFrequency: z.string().nullable(),
    estimatedFee: z.number().int().nullable(),
    estimatedFeeCurrency: currencySchema.nullable(),
  })),
  subItems: z.array(z.object({
    id: idSchema,
    title: z.string(),
    service: z.string().nullable(),
    freeZoneCode: z.string().nullable(),
    status: z.string(),
  })),
});
export type ProposalSourcesOutput = z.infer<typeof proposalSourcesOutputSchema>;

export const sendProposalInputSchema = z.object({
  envelopeId: idSchema,
});

export const listProposalsInputSchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).nullish(),
  engagementId: idSchema.optional(),
  clientId: idSchema.optional(),
  type: proposalTypeSchema.optional(),
  status: proposalStatusSchema.optional(),
  sortBy: z.enum(['subject', 'feeAmount', 'status', 'createdAt', 'signedAt']).default('createdAt'),
  sortOrder: sortOrderSchema,
});
export type ListProposalsInput = z.infer<typeof listProposalsInputSchema>;

export const declineProposalInputSchema = z.object({
  reason: z.string().max(1000).optional(),
});
export type DeclineProposalInput = z.infer<typeof declineProposalInputSchema>;

export const updateProposalInputSchema = createProposalInputSchema.partial().extend({
  /** Full-replacement sets while the proposal is still editable (DRAFT/SENT). */
  items: z.array(proposalItemInputSchema).min(1).max(100).optional(),
  clauses: z.array(proposalClauseInputSchema).max(50).optional(),
  billingSchedule: billingScheduleSchema.nullish(),
  billingStartDate: iso8601Schema.nullish(),
  pricingMode: proposalPricingModeSchema.optional(),
  lumpSumAmount: z.number().int().min(0).nullish(),
  // Compose allows an EMPTY body (.default('')) but createProposalInputSchema
  // requires min(1) — .partial() keeps that min when the edit card always
  // SENDS body, so empty-body proposals could never be saved from the edit
  // modal (400 at path 'body'). PATCH accepts the compose-valid empty string.
  body: z.string().max(10000).optional(),
});
export type UpdateProposalInput = z.infer<typeof updateProposalInputSchema>;
export const acceptProposalInputSchema = z.object({
  token: z.string().min(20),
  accepted: z.literal(true),
  signature: z.string().min(1).max(5000).optional(),
});
